import gql from 'graphql-tag';
import { useQuery, useResult, useMutation } from '@vue/apollo-composable';
import { useNPPackageInfo } from '@models/package/package';
import { usePackageInfo } from '@models/package/usePackageInfo';
import { useProfileInfo } from '@models/profile/useCurrentProfile';
import { ref, watch, computed } from 'vue';
import { cloneDeep } from 'lodash-es';
import {
    EngageV4ListMember,
    EngageV4VersionListMember,
    EngageV4ListVersion,
    EngageV4TypeEnum,
    Usergroup,
    EngageV4Version,
    Goal,
    GoalMember,
    EngageV4StopOtherVersionTrafficInput
} from './graphql.schema';
import { ExperienceVersion, Experience } from './experience';
import { sortBy } from 'lodash-es';
import { toThousand } from '@utils/common.util';
import i18n from '@assets/i18n';

export const experienceListConditions = ref();
export const canSaveListConditions = ref(true);

export type ExperienceStatusType = 'PAUSE' | 'SCHEDULED' | 'DRAFT' | 'RUNNING' | 'UNSCHEDULED';

export type ExperienceListInfoType = {
    id: string;
    sid?: string;
    name?: string;
    status?: any;
    createTime?: string;
    updateTime?: string;
    lastPauseTime?: string;
    firstRunTime?: string;
    firstRunABTestTime?: string;
    hasForm?: boolean;
    hasSync?: boolean;
    isVersionEvent?: boolean;
    createBy?: string;
    modifierBy?: string;
    hasVariant?: boolean;
    engageVersion?: string;
    engageType: EngageV4TypeEnum;
    baseURL?: string;
    screenshot?: string;
    enableABTest?: boolean;
    schedule?: JSON;
    conditions?: JSON;
    usergroups?: Usergroup[];
    versions?: ExperienceVersion[];
    goals?: Goal[];
    count?: number;
    engageView?: number | string;
    engageViewCount?: number | string;
    engageClick?: number | string;
    engageClose?: number | string;
    engageSubmit?: number | string;
    engageClickRate?: number | string;
    engageCloseRate?: number | string;
    engageSubmitRate?: string | number;
    goal?: GoalMember[];
    avgSessionDuration?: number;
    bounceRate?: string;
    sessionViewDepth?: string;
    thumbnail?: string;
    defaultGoal:
    | GoalMember
    | {
        name: string;
        number: string;
        rate: string;
    };
    activeGoalNumber?: string | number;
    activeGoalRate?: string | number;
    activeGoalName?: string | number;
    sourceView?: string | number;
    sourceViewCount?: string | number;
    sourceClick?: string | number;
    sourceClose?: string | number;
    sourceSubmit?: string | number;
    antiFlicker?: boolean;
};

export type UpdateEngageV4Input = {
    id: string;
    name?: string;
    status: ExperienceStatusType;
    antiFlicker?: boolean;
};

// const userGroup = `{
//     id
//     sid
//     name
//     source
//     content
//     description
//     createTime
//     updateTime
//     status
//     guideType
//     count
// }`;
const userGroup = `{
    type
    value
}`;

const Goal = `{
    id
    sid
    name
    content
    description
    createTime
    updateTime
    createBy
    modifierBy
    status
    count
}`;

const EngageV4CreativeTerminal = `{
    id
    type
    status
    popup
    stickybar
    inline
    code
    redirect
    engageVersionID
}`;

const EngageV4Version = `{
    id
    name
    rate
    controlGroup
    screenshot
    engageID
    terminals ${EngageV4CreativeTerminal}
}`;

const engageConfig = `{
    antiFlicker
    id
    sid
    name
    status
    createTime
    updateTime
    lastPauseTime
    firstRunTime
    hasForm
    hasSync
    isVersionEvent
    createBy
    modifierBy
    hasVariant
    engageVersion
    engageType
    baseURL
    screenshot
    schedule
    conditions
    usergroups ${userGroup}
    versions ${EngageV4Version}
    firstRunABTestTime
    enableABTest
    count
}`;

const engageData = `{
    engageViewCount
    engageView
    engageClick
    engageClickRate
    engageClose
    engageCloseRate
    engageSubmit
    engageSubmitRate
    goal {
        goalId
        rate
        name
        number
    }
    avgSessionDuration
    bounceRate
    sessionViewDepth
}`;

const np_package_info = `{
    sid
    ptxPackageId
    ptiPackageId
    startTime
    endTime
    resetTime
    ptxPrivilege {
        monthlyCampaignViewUsers
        campaigns
        userGroups
        usePopupForm
        useCampaignAbTest
        useCampaignUserGroupAndSession
        useCampaignCodeMode
        useCampaignJSTrigger
        useCampaignHeatmap
        useCampaignAI
        usePopup
        useStickyBar
        useInline
        useCampaignReport
        useCampaignReportCSVDownload
        useCampaignSettingPage
        usePopupStickyBarTrigger
        useCampaignSchedule
        useCampaignGoal
    }
    ptxPrivilegeUsage {
        monthlyCampaignViewUsers
        campaigns
        userGroups
    }
    ptiPrivilege {
        heatmaps
        HeatmapStorageMonths
        historicalHeatmaps
        useSegmentation
        useHeatmapCompetitor
        useSharableHeatmap
        useFunnel
        useHeatmapAbTest
        useHeatmap
        useRealtimeTracking
        useDataInsightsReport
        usePageGroup
        useConversionTracking
        useCampaignManagement
        useBasicSegmentation
    }
    ptiPrivilegeUsage {
        heatmaps
        historicalHeatmaps
    }
    ptiFreeTrial
    ptxFreeTrial
    trialEndTime
    bindWay
}`;

const engageV4VersionListMember = `{
    antiFlicker
    id
    name
    status
    engageType
    baseURL
    createTime
    updateTime
    lastPauseTime
    firstRunTime
    firstRunABTestTime
    schedule
    trafficMode
    hasSync
    versions {
        id
        name
        rate
        controlGroup
        screenshot
        terminals {
            id
            type
            status
            redirect
            engageVersionID
        }
        data {
            engageView
            engageViewCount
            engageClick
            engageClose
            engageSubmit
            engageClickRate
            engageCloseRate
            engageSubmitRate
            goal {
                goalId
                rate
                number
                name
                uplift
                isBest
                bestProbability
            }
            avgSessionDuration
            bounceRate
            sessionViewDepth
        }
    }
    engageData {
        engageView
        engageViewCount
        engageClick
        engageClose
        engageSubmit
        engageClickRate
        engageCloseRate
        engageSubmitRate
        goal {
                goalId
                rate
                number
                name
            }
        avgSessionDuration
        bounceRate
        sessionViewDepth
    }
}`;

const EngageV4TypePublish = `{
    engageType
    enableABTest
    templateSource
}`;

// graphql - 列表查询
const QUERY_EXPERIENCE_QUERY = gql`
    query queryEngageV4List {
        queryEngageV4List{
            engageConfig ${engageConfig},
            engageData ${engageData}
        }
    }`;

// graphql - experience复制
const COPY_EXPERIENCE_MUTATION = gql`
    mutation copyEngageV4($id: ID!) {
        copyEngageV4(id: $id)${engageConfig}
    }
`;

// graphql - experience删除
const DELETE_EXPERIENCE_MUTATION = gql`
    mutation deleteEngageV4($id: ID!) {
        deleteEngageV4(id: $id)${engageConfig}
    }
`;

// graphql - AB侧列表查询
const QUERY_AB_EXPERIENCE_QUERY = gql`
    query queryEngageV4VersionList {
        queryEngageV4VersionList ${engageV4VersionListMember}
    }`;

// graphql - experience发布
const PUBLISH_EXPERIENCE_MUTATION = gql`
    mutation publishEngageV4($id: ID!) {
        publishEngageV4(id: $id) ${EngageV4TypePublish}
    }
`;

// graphql - experience暂停
const PAUSE_EXPERIENCE_MUTATION = gql`
    mutation pauseEngageV4($id: ID!) {
        pauseEngageV4(id: $id)
    }
`;

// graphql - experience更新
const SCHEDULE_EXPERIENCE_MUTATION = gql`
    mutation saveAndScheduleEngageV4($id: ID!) {
        saveAndScheduleEngageV4(id: $id)
    }
`;

// graphql - 套餐查询
const GET_NP_PACKAGE_QUERY = gql`
    query getProfileNpPackageInfo($profileId: String!) {
        getProfileNpPackageInfo(profileId: $profileId)${np_package_info}
    }
`;

// graphql - 暂停其他版本
const PAUSE_OTHER_VERSION_MUTATION = gql`
    mutation stopOtherVersionTraffic($input: EngageV4StopOtherVersionTrafficInput!) {
        stopOtherVersionTraffic(input: $input)
    }
`;

const { getProfileNpPackageInfo } = useNPPackageInfo();
const { profileChangeLoading, profileInfo } = useProfileInfo();

const experienceData = ref<any | undefined>();
const experienceList = ref<EngageV4ListMember[] | undefined>();
const experienceListLoading = ref(false);
const deleteExperienceError = ref();
const publishExperienceError = ref();
const pauseExperienceError = ref();
const updateExperienceError = ref();

const experienceAbInfo = ref<EngageV4VersionListMember[] | undefined>();

// 切换档案清除数据
watch(
    () => profileChangeLoading.value,
    newStatus => {
        !newStatus && (experienceList.value = undefined);
    }
);

/**
 * 获取列表
 * @param resultCallback
 * @param rejectCallback
 */
export function queryExperienceList(
    resultCallback?: (T: any) => void,
    rejectCallback?: (err: any) => void
) {
    experienceListLoading.value = true;
    const { onError, onResult } = useQuery<{ queryEngageV4List: EngageV4ListMember[] }>(
        QUERY_EXPERIENCE_QUERY,
        null,
        {
            fetchPolicy: 'no-cache'
        }
    );

    onResult(data => {
        if (data.data?.queryEngageV4List) {
            experienceList.value = data.data?.queryEngageV4List?.map(
                (engage: EngageV4ListMember) => {
                    let _item = { ...engage.engageConfig, ...engage.engageData };
                    return transformListData(_item);
                }
            );
        }

        // 执行回调
        resultCallback &&
            typeof resultCallback === 'function' &&
            resultCallback(experienceList.value);

        experienceListLoading.value = false;
    });

    onError(err => {
        experienceList.value = undefined;
        experienceListLoading.value = false;
        rejectCallback && typeof rejectCallback === 'function' && rejectCallback(err);
    });
}

// experience列表
export function useExperienceList(query: boolean = true) {
    // 当前experience状态
    let experienceStatus: ExperienceStatusType = 'PAUSE';
    // 当前experienceId
    let experienceId: string = '';

    let exportFunc: any = {};

    const getExperienceData = (
        resultCallback?: (T: any) => void,
        rejectCallback?: (err: any) => void
    ) => {
        experienceListLoading.value = true;
        const { onError, onResult } = useQuery<{ queryEngageV4List: EngageV4ListMember[] }>(
            QUERY_EXPERIENCE_QUERY,
            null,
            {
                fetchPolicy: 'no-cache'
            }
        );

        onResult((data: any) => {
            experienceData.value = data.data?.queryEngageV4List.map(
                (engage: EngageV4ListMember) => {
                    let _item = { ...engage.engageConfig, ...engage.engageData };
                    return transformListData(_item);
                }
            );

            // 执行回调
            resultCallback &&
                typeof resultCallback === 'function' &&
                resultCallback(experienceData.value);

            experienceListLoading.value = false;
        });

        onError(err => {
            experienceData.value = undefined;
            rejectCallback && typeof rejectCallback === 'function' && rejectCallback(err);
            experienceListLoading.value = false;
        });
    };

    // 复制
    const copyExperience = ($id: string) => {
        const { mutate } = getCopyMutation();
        return mutate({ id: $id }).then((result: any) => {
            const { getProfilePackageInfo } = usePackageInfo();
            const { getProfileNpPackageInfo } = useNPPackageInfo();
            // 更新套餐
            getProfilePackageInfo();
            getProfileNpPackageInfo();
            return result.data.copyEngageV4;
        });
    };

    // 删除
    const deleteExperience = ($id: string) => {
        experienceId = $id;
        const { mutate, onError } = getDeleteMutation($id);
        deleteExperienceError.value = onError;
        return mutate({ id: $id }).then((result: any) => {
            // 更新套餐
            getProfileNpPackageInfo();
            return result.data.deleteEngageV4;
        });
    };

    // 发布experience
    const publishExperience = ($id: string) => {
        experienceId = $id;
        experienceStatus = 'RUNNING';
        const { mutate, onError } = getUpdateMutation($id, experienceStatus);
        publishExperienceError.value = onError;
        return mutate({ id: $id }).then((result: any) => {
            return result.data.publishEngageV4;
        });
    };

    // 暂停experience
    const pauseExperience = ($id: string) => {
        experienceId = $id;
        experienceStatus = 'PAUSE';
        const { mutate, onError } = getPauseMutation($id, experienceStatus);
        pauseExperienceError.value = onError;
        return mutate({ id: $id }).then((result: any) => {
            return result.data.pauseEngageV4;
        });
    };

    // schedule experience
    const updateExperience = (param: UpdateEngageV4Input) => {
        experienceId = param.id;
        const _status: any = param.status.toUpperCase();
        experienceStatus = _status;

        const { mutate, onError } = getScheduleMutation(param.id, experienceStatus);
        updateExperienceError.value = onError;
        return mutate({ id: param.id }).then((result: any) => {
            return result.data.saveAndScheduleEngageV4;
        });
    };

    const updateExperienceListByManual = (id: string, key: any, value: any) => {
        const item = experienceList?.value?.find((item: any) => item.id === id) as any;
        if (item) {
            item[key] = value;
        }
    }

    return {
        ...exportFunc,

        experienceList,
        experienceListInfo: experienceList,
        refetchExperienceList: queryExperienceList,
        experienceListLoading,

        queryExperienceList,

        experienceData,
        getExperienceData,

        copyExperience,
        deleteExperience,
        deleteExperienceError,
        pauseExperience,
        pauseExperienceError,
        publishExperience,
        publishExperienceError,
        updateExperience,
        updateExperienceError,
        updateExperienceListByManual
    };
}

// experienceAB侧列表
export function getExperienceAbList() {
    const {
        result,
        refetch: refetchExperience,
        loading: experienceABLoading,
        error: experienceError,
        onError: onExperienceError,
        onResult: onExperienceResult,
    } = useQuery<{ queryEngageV4VersionList: EngageV4VersionListMember[] }>(
        QUERY_AB_EXPERIENCE_QUERY,
        null,
        {
            fetchPolicy: 'cache-and-network',
            notifyOnNetworkStatusChange: true
        }
    );

    onExperienceResult(data => {
        if (data?.data?.queryEngageV4VersionList) {
            experienceAbInfo.value = data.data.queryEngageV4VersionList?.map(
                (e: EngageV4VersionListMember) => {
                    e.versions = sortBy(e.versions, function (version: EngageV4ListVersion) {
                        return !version.controlGroup;
                    });
                    return { ...e, ...e.engageData };
                }
            );
        }
    });

    const updateAbListByManual = (id: string, key: any, value: any) => {
        const item = experienceAbInfo?.value?.find((item: any) => item.id === id) as any;
        if (item) {
            item[key] = value;
        }
    }

    return {
        experienceAbInfo,
        refetchExperience,
        experienceABLoading,
        experienceError,
        onExperienceError,
        onExperienceResult,
        updateAbListByManual,
    };
}

// 暂停其他版本
export function pauseOtherVersions(input: EngageV4StopOtherVersionTrafficInput) {
    const { mutate: stopOtherVersionMutate } = useMutation(PAUSE_OTHER_VERSION_MUTATION);

    return stopOtherVersionMutate(
        { input },
        {
            update: (cache, { data: { stopOtherVersionTraffic } }) => {
                if (!stopOtherVersionTraffic) return;
                // 更新AB侧流量分配
                const engageV4AbListData: {
                    queryEngageV4VersionList: EngageV4VersionListMember[];
                } | null = cache.readQuery({
                    query: QUERY_AB_EXPERIENCE_QUERY
                });

                const _index: number | undefined =
                    engageV4AbListData?.queryEngageV4VersionList.findIndex(
                        (engage: EngageV4VersionListMember) => {
                            return engage.id === input.id;
                        }
                    );
                if (_index !== undefined && _index > -1 && engageV4AbListData) {
                    engageV4AbListData.queryEngageV4VersionList[_index].versions =
                        engageV4AbListData?.queryEngageV4VersionList[_index].versions?.map(
                            (version: EngageV4ListVersion) => {
                                version.rate = version.id === input.versionId ? 1 : 0;
                                return version;
                            }
                        );
                }

                cache.writeQuery({
                    query: QUERY_AB_EXPERIENCE_QUERY,
                    data: engageV4AbListData
                });
            }
        }
    ).then((result: any) => {
        return result.data.stopOtherVersionTraffic;
    });
}

// 获取套餐信息
export function getNpPackageInfo(profileId: string | undefined) {
    const {
        result,
        refetch: refetchNpPackage,
        loading: npPackageInfoLoading,
        onResult: onNpPackageResult
    } = useQuery<{ getProfileNpPackageInfo: any }>(GET_NP_PACKAGE_QUERY, {
        profileId
    });
    const npPackageInfo = useResult(result, null, data => {
        return data?.getProfileNpPackageInfo;
    });
    return {
        npPackageInfo,
        refetchNpPackage,
        npPackageInfoLoading,
        onNpPackageResult
    };
}

const transformListData = (_item: any) => {
    return {
        ..._item,
        thumbnail: _item.screenshot,
        bounceRate:
            _item.status !== 'DRAFT' ? ((_item.bounceRate ?? 0) * 100)?.toFixed(2) + '%' : '--',
        sessionViewDepth:
            _item.status !== 'DRAFT' ? (_item.sessionViewDepth ?? 0).toFixed(2) : '--',
        engageSubmitRate:
            _item.status !== 'DRAFT'
                ? ((_item.engageSubmitRate ?? 0) * 100)?.toFixed(2) + '%'
                : '--',
        engageClickRate:
            _item.status !== 'DRAFT'
                ? ((_item.engageClickRate ?? 0) * 100)?.toFixed(2) + '%'
                : '--',
        engageCloseRate:
            _item.status !== 'DRAFT'
                ? ((_item.engageCloseRate ?? 0) * 100)?.toFixed(2) + '%'
                : '--',
        sourceView: _item.engageView,
        engageView:
            _item.status !== 'DRAFT' ? _item.engageView && toThousand(_item.engageView) : '--',
        sourceViewCount: _item.engageViewCount,
        engageViewCount:
            _item.status !== 'DRAFT'
                ? _item.engageViewCount && toThousand(_item.engageViewCount)
                : '--',
        sourceClick: _item.engageClick,
        engageClick:
            _item.status !== 'DRAFT' ? _item.engageClick && toThousand(_item.engageClick) : '--',
        sourceClose: _item.engageClose,
        engageClose:
            _item.status !== 'DRAFT' ? _item.engageClose && toThousand(_item.engageClose) : '--',
        sourceSubmit: _item.engageSubmit,
        engageSubmit:
            _item.status !== 'DRAFT' ? _item.engageSubmit && toThousand(_item.engageSubmit) : '--',
        activeGoalNumber: _item.goal && _item.goal.length ? _item.goal[0]?.number : '--',
        activeGoalRate: _item.goal && _item.goal.length ? _item.goal[0]?.rate : '--',
        activeGoalName: _item.goal && _item.goal.length ? _item.goal[0]?.name : '--',
        defaultGoal:
            _item.goal && _item.goal.length
                ? _item.goal[0]
                : { name: '--', number: '--', rate: '--' }
    };
};

export const initExperienceListConditions = () => {
    canSaveListConditions.value = true;
};

export const setExperienceListConditions = (conditions: any) => {
    if (canSaveListConditions.value) {
        experienceListConditions.value = {
            ...experienceListConditions.value,
            ...cloneDeep(conditions)
        };
    }
};

export const clearExperienceListConditions = () => {
    canSaveListConditions.value = false;
    experienceListConditions.value = null;
};

export const updateStatusChangeData = (experienceId: string, attributeParam: any) => {
    if (!experienceId) return;
    const config: any = experienceList.value?.find((engage: any) => engage.id === experienceId);
    if (config) {
        config.updateTime = new Date().getTime();
        Object.keys(attributeParam).map(key => {
            if (key === 'status') {
                const experienceStatus = attributeParam[key];
                if (experienceStatus === 'RUNNING' && !config.firstRunTime) {
                    config.firstRunTime = new Date().getTime();
                }
                if (experienceStatus === 'PAUSE') {
                    config.lastPauseTime = new Date().getTime();
                    config.status =
                        config.status === 'SCHEDULED' && !config.firstRunTime ? 'DRAFT' : 'PAUSE';
                } else {
                    config.status = experienceStatus;
                }
            } else {
                config[key] = attributeParam[key];
            }
        });
    }

    // 更新experience AB侧缓存数据
    const list: any = experienceAbInfo.value?.find((engage: EngageV4VersionListMember) => {
        return engage.id === experienceId;
    });
    if (list) {
        list.updateTime = new Date().getTime();
        Object.keys(attributeParam).map(key => {
            if (key === 'status') {
                const experienceStatus = attributeParam[key];
                if (experienceStatus === 'RUNNING' && !list.firstRunTime) {
                    list.firstRunTime = new Date().getTime();
                }
                if (experienceStatus === 'PAUSE') {
                    list.lastPauseTime = new Date().getTime();
                    list.status =
                        list.status === 'SCHEDULED' && !list.firstRunTime ? 'DRAFT' : 'PAUSE';
                } else {
                    list.status = experienceStatus;
                }
            } else {
                list[key] = attributeParam[key];
            }
        });
    }
};

function getCopyMutation() {
    return useMutation(COPY_EXPERIENCE_MUTATION);
}

function getDeleteMutation(experienceId: string) {
    return useMutation(DELETE_EXPERIENCE_MUTATION, () => ({
        update: (cache, { data: { deleteEngageV4 } }) => {
            const { getProfilePackageInfo } = usePackageInfo();
            const { getProfileNpPackageInfo } = useNPPackageInfo();
            getProfilePackageInfo();
            getProfileNpPackageInfo();

            if (!experienceId || !deleteEngageV4) return;
            const _index: number | undefined = experienceList.value?.findIndex(
                (engage: any) => engage.id === experienceId
            );
            if (_index !== undefined && _index > -1) {
                experienceList.value?.splice(_index, 1);
            }

            // 更新experience AB侧缓存数据
            const engageV4AbListData: {
                queryEngageV4VersionList: EngageV4VersionListMember[];
            } | null = cache.readQuery({
                query: QUERY_AB_EXPERIENCE_QUERY
            });
            if (!experienceId || !deleteEngageV4) return;
            const _index2: number | undefined =
                engageV4AbListData?.queryEngageV4VersionList.findIndex(
                    (engage: EngageV4VersionListMember) => {
                        return engage.id === experienceId;
                    }
                );
            if (_index2 !== undefined && _index2 > -1) {
                engageV4AbListData?.queryEngageV4VersionList.splice(_index2, 1);
                cache.writeQuery({
                    query: QUERY_AB_EXPERIENCE_QUERY,
                    data: engageV4AbListData
                });
            }
        }
    }));
}

function getUpdateMutation(experienceId: string, experienceStatus: ExperienceStatusType) {
    return useMutation(PUBLISH_EXPERIENCE_MUTATION, () => ({
        update: (cache, { data: { publishEngageV4 } }) => {
            publishEngageV4 && updateStatusChangeData(experienceId, { status: experienceStatus });
        }
    }));
}

function getPauseMutation(experienceId: string, experienceStatus: ExperienceStatusType) {
    return useMutation(PAUSE_EXPERIENCE_MUTATION, () => ({
        update: (cache, { data: { pauseEngageV4 } }) => {
            pauseEngageV4 && updateStatusChangeData(experienceId, { status: experienceStatus });
        }
    }));
}

function getScheduleMutation(experienceId: string, experienceStatus: ExperienceStatusType) {
    return useMutation(SCHEDULE_EXPERIENCE_MUTATION, () => ({
        update: (cache, { data: { saveAndScheduleEngageV4 } }) => {
            saveAndScheduleEngageV4 &&
                updateStatusChangeData(experienceId, { status: experienceStatus });
        }
    }));
}
